import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, patch, post } from '@/shared/api/api-client';
import { parseValidationProblem } from '@/shared/lib/toast-helpers';
import type { ResourceRecord } from '@/settings/components/resources/ResourceCard';
import { resourcesListQueryKey } from './use-resources-list';

/** The device form's fields, as the server names them in its messages. */
export type ResourceField = 'name' | 'subtype' | 'identifier' | 'roomId' | 'technicianId';

/**
 * A create / update the server refused, attributed to a field where it can be.
 *
 * `ResourceService` throws `PracticeManagementException` for every domain
 * rule (duplicate name, unknown / inactive room, technician not at this
 * practice, over-long text); `HttpGlobalExceptionFilter` turns each into a 400
 * `ValidationProblemDetails` with the message under `errors.DomainValidations`.
 * A 409 is kept for a duplicate name should the route ever answer that way.
 * `field` is derived from the message text so the form can pin it to the
 * offending control instead of a toast; `null` when it cannot tell.
 */
export class ResourceSaveError extends Error {
  status: number;
  field: ResourceField | null;

  constructor(message: string, status: number, field: ResourceField | null) {
    super(message);
    this.name = 'ResourceSaveError';
    this.status = status;
    this.field = field;
  }
}

/**
 * Which control a server message is about. Exported for the form's tests.
 *
 * The duplicate message interpolates the device's own name —
 * `A device named "{name}" already exists at this practice.` — so quoted text
 * is stripped before matching and the duplicate case is tested first;
 * otherwise a device called "Treatment Room Scanner" would land on the Room
 * field.
 */
export function fieldForMessage(message: string): ResourceField | null {
  const m = message.replace(/"[^"]*"/g, '').toLowerCase();
  if (m.includes('already exists')) return 'name';
  if (m.includes('technician')) return 'technicianId';
  if (/\broom\b/.test(m)) return 'roomId';
  if (m.includes('subtype')) return 'subtype';
  if (m.includes('identifier')) return 'identifier';
  if (m.includes('name')) return 'name';
  return null;
}

/**
 * Both write mutations opt out of the global `MutationCache.onError` toast
 * (`query-client.ts`): the form owns the messaging — an attributed
 * `ResourceSaveError` is shown on its field and anything else is toasted by
 * the form itself — so the generic "Something went wrong" must not stack on
 * top. Same opt-out as `use-check-duplicate-contact.ts`.
 */
const OWN_ERROR_HANDLING = { skipGlobalError: true } as const;

/**
 * Map an `ApiError` from the resource write routes to a `ResourceSaveError`.
 * Anything that is not a 400 / 409 (network, 401 that survived the refresh,
 * 5xx) is returned unchanged so the generic handling still applies.
 */
export function toResourceSaveError(err: unknown): unknown {
  if (!(err instanceof ApiError)) return err;
  if (err.status === 400) {
    const message = parseValidationProblem(err.body) ?? 'The device could not be saved.';
    return new ResourceSaveError(message, 400, fieldForMessage(message));
  }
  if (err.status === 409) {
    const body = err.body?.trim() ?? '';
    const message =
      parseValidationProblem(err.body) ??
      (body && body.length < 200 && !body.startsWith('{')
        ? body
        : 'A device with this name already exists at this practice.');
    return new ResourceSaveError(message, 409, 'name');
  }
  return err;
}

export interface CreateResourceInput {
  practiceId: string;
  name: string;
  subtype?: string | null;
  identifier?: string | null;
  roomId?: string | null;
  technicianId?: string | null;
}

/**
 * Provision one device — PROD-2010, revised for PROD-2008 / PracticeManagement
 * PR #944 (ruling 2026-09-11: a resource IS a device; rooms are places).
 *
 *   POST /api/Resource   body: { practiceId, name, subtype?, identifier?, roomId?, technicianId? }
 *
 * `type` is deliberately NOT sent: the server defaults it to Device and there
 * is no other kind. Optional fields the user left blank are omitted rather
 * than sent as `null` — a create has nothing to clear.
 *
 * Per-entity mutation — one device per call. There is deliberately no bulk
 * save and no `deletedIds[]` route (QA-RES-027).
 *
 * The acting user travels in the `UserId` request header, which `api-client`
 * already sets from the auth store — the controller binds it with
 * `[FromHeader] Guid userId`, so it must NOT be duplicated into the body.
 */
export function useCreateResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateResourceInput) => {
      const body: Record<string, unknown> = {
        practiceId: input.practiceId,
        name: input.name,
      };
      if (input.subtype) body.subtype = input.subtype;
      if (input.identifier) body.identifier = input.identifier;
      if (input.roomId) body.roomId = input.roomId;
      if (input.technicianId) body.technicianId = input.technicianId;
      try {
        return await post<ResourceRecord>('/api/Resource', body);
      } catch (err) {
        throw toResourceSaveError(err);
      }
    },
    meta: OWN_ERROR_HANDLING,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: resourcesListQueryKey(vars.practiceId),
      });
    },
  });
}

export interface UpdateResourceInput {
  id: string;
  /** Only used to target the cache key for invalidation. */
  practiceId: string;
  name: string;
  /** `null` clears; `undefined` leaves the stored value untouched. */
  subtype?: string | null;
  identifier?: string | null;
  roomId?: string | null;
  technicianId?: string | null;
}

/**
 * Update one device's editable fields — name, subtype, identifier, room,
 * technician.
 *
 *   PATCH /api/Resource/{id}   body: { name, subtype?, identifier?, roomId?, technicianId? }
 *
 * Fields are written as sent: `roomId: null` clears the room, an omitted key
 * leaves it unchanged, and an unchanged room / technician is never
 * re-validated server-side (so a rename cannot fail because the room was
 * deactivated later). Type and status never travel here: type is immutable,
 * and status changes go through Archive / Restore.
 */
export function useUpdateResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateResourceInput) => {
      const body: Record<string, unknown> = { name: input.name };
      if (input.subtype !== undefined) body.subtype = input.subtype;
      if (input.identifier !== undefined) body.identifier = input.identifier;
      if (input.roomId !== undefined) body.roomId = input.roomId;
      if (input.technicianId !== undefined) body.technicianId = input.technicianId;
      try {
        return await patch<ResourceRecord>(`/api/Resource/${input.id}`, body);
      } catch (err) {
        throw toResourceSaveError(err);
      }
    },
    meta: OWN_ERROR_HANDLING,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: resourcesListQueryKey(vars.practiceId),
      });
    },
  });
}
